import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { isBetslipResponses } from '@core/Data/betslip'

export interface FavouriteNumbersBettingState {
  gameOne: number[]
  gameTwo: number[]
  pools: Date[]
  potLuckBets: number
  betConfirmed: boolean
  resultKey: string
  showAllPools: boolean
  savedFavouriteNumbers: { gameOne: number[]; gameTwo: number[] }
}

export const initialState: FavouriteNumbersBettingState = {
  gameOne: [],
  gameTwo: [],
  pools: [],
  potLuckBets: 1,
  betConfirmed: false,
  resultKey: 'current',
  showAllPools: false,
  savedFavouriteNumbers: { gameOne: [], gameTwo: [] },
}

const slice = createSlice({
  name: 'favouriteNumbers',
  initialState,
  reducers: {
    toggleGameNumber(state, action: PayloadAction<{ game: number; number: number }>) {
      const { game, number } = action.payload
      const isFirstGame = game === 1
      const currentGame = isFirstGame ? state.gameOne : state.gameTwo

      let updatedGame: number[]

      if (currentGame.includes(number)) {
        updatedGame = currentGame.filter(x => x !== number)
      } else if (currentGame.length < 16) {
        updatedGame = [...currentGame, number].sort((a, b) => a - b)
      } else {
        updatedGame = [...currentGame]
      }

      if (isFirstGame) {
        state.gameOne = updatedGame
      } else {
        state.gameTwo = updatedGame
      }
    },

    togglePool(state, action: PayloadAction<Date>) {
      const pools = state.pools
      let updatedPools: Date[]

      const alreadySelected = pools.includes(action.payload)
      const moreThanOne = pools.length > 1

      if (alreadySelected && moreThanOne) {
        updatedPools = pools.filter(x => x !== action.payload)
      } else if (!alreadySelected) {
        updatedPools = [...pools, action.payload]
      } else {
        updatedPools = [...pools]
      }
      updatedPools.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      state.pools = updatedPools
    },

    deselectPool(state, action: PayloadAction<Date>) {
      const pools = state.pools
      const selectedPools = pools.filter(x => x !== action.payload)
      state.pools = selectedPools
    },

    setNumberOfBets(state, action: PayloadAction<number>) {
      state.potLuckBets = action.payload
    },

    setPotLuckNumbers(state, action: PayloadAction<number>) {
      state.gameOne = [action.payload]
    },

    clearSelections(state) {
      return {
        ...initialState,
        savedFavouriteNumbers: state.savedFavouriteNumbers,
      }
    },

    setResultKey(state, action: PayloadAction<string>) {
      state.resultKey = action.payload
    },

    setShowAll(state, action: PayloadAction<boolean>) {
      state.showAllPools = action.payload
    },

    loadNumbers(state, action: PayloadAction<{ numbers: number[]; game: number }>) {
      const { numbers, game } = action.payload
      if (game === 1) {
        state.gameOne = numbers
      } else {
        state.gameTwo = numbers
      }
    },

    saveFavouriteNumbers(state, action: PayloadAction<{ numbers: number[]; game: number }>) {
      const { numbers, game } = action.payload
      if (game === 1) {
        state.savedFavouriteNumbers.gameOne = numbers
      } else {
        state.savedFavouriteNumbers.gameTwo = numbers
      }
    },

    confirmBetSlipBetsSucceeded(state) {
      if (isBetslipResponses(state)) {
        state.betConfirmed = true
      }
    },

    quickbetClosed(state) {
      if (state.betConfirmed) {
        return initialState
      }
      return state
    },
  },
})

export const {
  toggleGameNumber,
  togglePool,
  deselectPool,
  setNumberOfBets,
  setPotLuckNumbers,
  clearSelections,
  setResultKey,
  setShowAll,
  loadNumbers,
  saveFavouriteNumbers,
} = slice.actions

export default persistReducer(
  {
    key: 'savedFavouriteNumbers',
    whitelist: ['savedFavouriteNumbers'],
    storage,
    debug: !PRODUCTION,
  },
  slice.reducer
)
